// Only used for Unity apps

import { createContext, ReactNode, useContext, useMemo } from 'react'
import { usePageFilterIdsQuery } from '@/hooks/useFilters/useFilters.generated'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { LoadingComponent } from '@/components/ui/LoadingComponent'
import { usePlatformOutletContext } from '@/ux/Platform/usePlatFormOutletContext'

type FilterIdContextType = {
  hierarchicalFilterIds: string[]
  primaryFilterIds: string[]
  filterIds: string[]
}

const FilterIdsContext = createContext<FilterIdContextType | null>({
  hierarchicalFilterIds: [],
  primaryFilterIds: [],
  filterIds: [],
})

export const FilterIdsProvider = ({ children }: { children: ReactNode }) => {
  const { appKey, pageId, projectId } = usePlatformOutletContext()
  const { data, loading, error } = usePageFilterIdsQuery({
    variables: {
      appId: appKey,
      pageId,
      projectId,
    },
  })

  const { hierarchicalFilterIds = [], primaryFilterIds = [], filterIds = [] } = data?.app?.pageById ?? {}
  const contextValue = useMemo(() => {
    return {
      hierarchicalFilterIds: hierarchicalFilterIds ?? [],
      primaryFilterIds: primaryFilterIds ?? [],
      filterIds: filterIds ?? [],
    }
  }, [filterIds, hierarchicalFilterIds, primaryFilterIds])

  if (loading) return <LoadingComponent />

  if (error) return <ErrorComponent />

  return <FilterIdsContext.Provider value={contextValue}>{children}</FilterIdsContext.Provider>
}

export const useFilterIds = () => {
  const context = useContext(FilterIdsContext)
  if (!context) {
    throw new Error('useFilterIds must be used within a FilterIdsProvider')
  }
  return context
}
